import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { auth, functions } from "../firebase";
import { useProperty } from "../context/PropertyContext";

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const MIN_WRITE_INTERVAL_MS = 25 * 1000;

function getSessionId() {
  try {
    const existing = window.sessionStorage.getItem("managerlogixUsageSessionId");
    if (existing) return existing;
    const next =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem("managerlogixUsageSessionId", next);
    return next;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function resolveModule(pathname) {
  const path = String(pathname || "").toLowerCase();
  if (path.startsWith("/select-property")) return "property_selector";
  if (path.startsWith("/dashboard")) return "dashboard";
  if (path.startsWith("/inspections") || path === "/monthly" || path === "/quarterly" || path === "/annual") return "inspections";
  if (path.startsWith("/leases") || path.includes("lease")) return "leases";
  if (path.startsWith("/work-orders") || path.startsWith("/vendors")) return "work_orders";
  if (path.startsWith("/financials") || path.startsWith("/expenses") || path.startsWith("/revenue") || path.startsWith("/rent-tracker")) return "financials";
  if (path.startsWith("/entities")) return "entities";
  if (path.startsWith("/reports") || path.startsWith("/metrics") || path.startsWith("/yearly-summary")) return "reports";
  if (path.startsWith("/notices")) return "notices";
  if (path.startsWith("/documents")) return "documents";
  if (path.startsWith("/tasks")) return "tasks";
  if (path.startsWith("/tenant-messages")) return "tenant_messages";
  if (path.startsWith("/property-settings") || path.startsWith("/rename-suites")) return "property_settings";
  if (path.startsWith("/marketing")) return "marketing";
  if (path.startsWith("/tenant-dashboard")) return "tenant_portal";
  return "app";
}

export default function UsageTracker({ enabled = true, userRole = "", loginMode = "" }) {
  const location = useLocation();
  const { activeProperty, currentUserEmailLower } = useProperty();
  const sessionIdRef = useRef(getSessionId());
  const lastInteractionAtRef = useRef(Date.now());
  const lastWriteAtRef = useRef(0);
  const recordFnRef = useRef(null);

  const moduleName = useMemo(() => resolveModule(location.pathname), [location.pathname]);
  const propertyId = activeProperty && activeProperty !== "all" ? String(activeProperty) : "";

  const markInteraction = useCallback(() => {
    lastInteractionAtRef.current = Date.now();
  }, []);

  const record = useCallback(
    async (action, { force = false } = {}) => {
      const user = auth.currentUser;
      if (!enabled || !user || !currentUserEmailLower) return;

      const now = Date.now();
      if (!force && now - lastWriteAtRef.current < MIN_WRITE_INTERVAL_MS) return;
      if (!force && now - lastInteractionAtRef.current > INACTIVITY_TIMEOUT_MS) return;

      lastWriteAtRef.current = now;

      try {
        if (!recordFnRef.current) {
          recordFnRef.current = httpsCallable(functions, "recordUsageActivity");
        }
        await recordFnRef.current({
          sessionId: sessionIdRef.current,
          action,
          module: moduleName,
          // Query strings can contain invitation, OAuth, and signing tokens.
          route: location.pathname,
          propertyId,
          loginMode: String(loginMode || ""),
          role: String(userRole || ""),
        });
      } catch (error) {
        // Usage analytics must never interrupt normal app work.
        console.warn("[UsageTracker] Activity tracking skipped", error);
      }
    },
    [currentUserEmailLower, enabled, location.pathname, location.search, loginMode, moduleName, propertyId, userRole]
  );

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, markInteraction, { passive: true }));
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") markInteraction();
      if (document.visibilityState === "hidden") record("tab_hidden", { force: true });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, markInteraction));
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [markInteraction, record]);

  useEffect(() => {
    markInteraction();
    record(propertyId ? "selected_property" : "viewed_module", { force: true });
  }, [location.pathname, location.search, propertyId, markInteraction, record]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      record("heartbeat");
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [record]);

  return null;
}
